import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Dumbbell, Music, Trees, Waves, Trophy, Film, Palette, PartyPopper, ArrowRight, Check, MapPin, Search } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Space category data with icons
const spaceCategories = [
  { name: 'Home Gym', slug: 'home-gym', icon: Dumbbell, image: 'https://images.unsplash.com/photo-1540497077202-7c8a3999166f?w=800&q=80' },
  { name: 'Studio', slug: 'studio', icon: Music, image: 'https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?w=800&q=80' },
  { name: 'Backyard', slug: 'backyard', icon: Trees, image: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800&q=80' },
  { name: 'Pool', slug: 'pool', icon: Waves, image: 'https://images.unsplash.com/photo-1576013551627-0cc20b96c2a7?w=800&q=80' },
  { name: 'Tennis Court', slug: 'tennis-court', icon: Trophy, image: 'https://images.unsplash.com/photo-1554068865-24cecd4e34b8?w=800&q=80' },
  { name: 'Home Theater', slug: 'home-theater', icon: Film, image: 'https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?w=800&q=80' },
  { name: 'Creative Space', slug: 'creative-space', icon: Palette, image: 'https://images.unsplash.com/photo-1513364776144-60967b0f800f?w=800&q=80' },
  { name: 'Event Space', slug: 'event-space', icon: PartyPopper, image: 'https://images.unsplash.com/photo-1519167758481-83f550bb49b3?w=800&q=80' },
];

export default function HomePage() {
  const navigate = useNavigate();
  const [location, setLocation] = useState('');
  const [category, setCategory] = useState('');
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    // Fetch categories from API
    fetch(`${API_URL}/api/categories`)
      .then(res => res.json())
      .then(data => setCategories(data))
      .catch(err => console.error('Failed to load categories:', err));
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (location) params.set('location', location);
    if (category) params.set('category', category);
    navigate(`/search?${params.toString()}`);
  };

  return (
    <div className="min-h-screen bg-[#050505] font-sans">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1920&q=80" 
            alt="Luxury home" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/90 to-[#050505]/50" />
        </div>
        
        {/* Hero Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-24">
          <div className="max-w-3xl">
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-light tracking-tight text-white leading-[1.1] mb-6">
              Turn Private Spaces Into <span className="text-[#D4AF37]">Opportunity</span>
            </h1>
            <p className="text-lg md:text-xl font-light text-zinc-300 leading-relaxed mb-8 max-w-2xl">
              100Nook is a marketplace where homeowners list unused private spaces — like home gyms, studios, backyards, and more — and guests can book them easily.
            </p>
            
            {/* Icon Row */}
            <div className="flex flex-wrap gap-4 mb-10">
              {spaceCategories.slice(0, 7).map((category) => (
                <div key={category.name} className="flex items-center gap-2 text-zinc-400 text-sm">
                  <category.icon size={18} className="text-[#D4AF37]" />
                  <span>{category.name}</span>
                </div>
              ))}
            </div>
            
            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-4">
              <Link 
                to="/become-host" 
                data-testid="hero-become-host-btn"
                className="bg-[#D4AF37] text-[#050505] font-medium px-8 py-4 rounded-full transition-all hover:bg-[#E5C564] hover:shadow-[0_0_20px_rgba(212,175,55,0.4)] inline-flex items-center gap-2"
              >
                Become a Host
                <ArrowRight size={18} />
              </Link>
              <Link 
                to="/search" 
                data-testid="hero-find-space-btn"
                className="bg-transparent text-[#D4AF37] border border-[#D4AF37]/30 font-medium px-8 py-4 rounded-full transition-all hover:border-[#D4AF37] hover:bg-[#D4AF37]/5 inline-flex items-center gap-2"
              >
                Find a Space
              </Link>
              <Link 
                to="/affiliate" 
                data-testid="hero-affiliate-btn"
                className="bg-transparent text-white border border-white/20 font-medium px-8 py-4 rounded-full transition-all hover:border-white/40 hover:bg-white/5 inline-flex items-center gap-2"
              >
                Become an Affiliate
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Search Widget Section */}
      <section className="py-16 md:py-20 bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="text-center mb-8">
            <h2 className="font-serif text-2xl md:text-3xl font-light text-white mb-2">
              Find Your Perfect Space
            </h2>
            <p className="text-zinc-400">Search by location or browse by category</p>
          </div>
          
          <form onSubmit={handleSearch} className="bg-[#141414] border border-[#D4AF37]/20 rounded-2xl p-4 md:p-6">
            <div className="flex flex-col md:flex-row gap-4">
              {/* Location Input */}
              <div className="flex-1 flex items-center gap-3 bg-[#0A0A0A] border border-white/10 rounded-xl px-4 py-3">
                <MapPin size={20} className="text-[#D4AF37]" />
                <input
                  type="text"
                  data-testid="home-search-location"
                  placeholder="City or Zip Code"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  className="w-full bg-transparent text-white placeholder-zinc-500 focus:outline-none"
                />
              </div>
              
              {/* Category Select */}
              <div className="flex-1 flex items-center gap-3 bg-[#0A0A0A] border border-white/10 rounded-xl px-4 py-3">
                <Search size={20} className="text-[#D4AF37]" />
                <select
                  data-testid="home-search-category"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full bg-transparent text-white focus:outline-none cursor-pointer"
                >
                  <option value="" className="bg-[#0A0A0A]">All Categories</option>
                  {categories.map((cat) => (
                    <option key={cat.slug} value={cat.slug} className="bg-[#0A0A0A]">
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Search Button */}
              <button
                type="submit"
                data-testid="home-search-btn"
                className="bg-[#D4AF37] text-[#050505] font-medium px-8 py-3 rounded-xl transition-all hover:bg-[#E5C564] hover:shadow-[0_0_20px_rgba(212,175,55,0.4)] flex items-center justify-center gap-2"
              >
                <Search size={20} />
                <span>Search</span>
              </button>
            </div>
          </form>
          
          {/* Quick Category Links */}
          <div className="flex flex-wrap justify-center gap-3 mt-6">
            {spaceCategories.slice(0, 6).map((cat) => (
              <Link
                key={cat.slug}
                to={`/search?category=${cat.slug}`}
                className="flex items-center gap-2 px-4 py-2 bg-[#141414] border border-white/10 rounded-full text-sm text-zinc-400 hover:text-white hover:border-[#D4AF37]/40 transition-all"
              >
                <cat.icon size={14} className="text-[#D4AF37]" />
                <span>{cat.name}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* What is 100Nook Section */}
      <section className="py-24 md:py-32 border-t border-[#D4AF37]/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#D4AF37] mb-4">What is 100Nook</p>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-light text-white mb-6">
              Connecting Spaces with People
            </h2>
            <p className="text-lg md:text-xl font-light text-zinc-400 leading-relaxed">
              100Nook connects homeowners with unused private spaces to people who need them — creating flexible income for hosts and unique experiences for guests.
            </p>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-24 md:py-32 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#D4AF37] mb-4">How It Works</p>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-light text-white">
              Simple as 1, 2, 3
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { step: '01', title: 'Hosts list unused spaces', desc: 'Create a listing with photos, availability, and pricing' },
              { step: '02', title: 'Guests send booking requests', desc: 'Browse spaces and request the perfect one for your needs' },
              { step: '03', title: 'Hosts approve and earn income', desc: 'Accept bookings and start earning from your space' },
            ].map((item, index) => (
              <div 
                key={index}
                className="bg-[#141414] border border-[#D4AF37]/15 rounded-2xl p-8 transition-all duration-300 hover:-translate-y-1 hover:border-[#D4AF37]/40"
              >
                <span className="text-6xl font-serif font-light text-[#D4AF37]/30">{item.step}</span>
                <h3 className="font-serif text-xl md:text-2xl text-white mt-4 mb-3">{item.title}</h3>
                <p className="text-zinc-400 font-light">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits for Hosts Section */}
      <section className="py-24 md:py-32 border-t border-[#D4AF37]/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#D4AF37] mb-4">For Hosts</p>
              <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-light text-white mb-6">
                Earn From Space You Already Own
              </h2>
              <ul className="space-y-4 mb-8">
                {[
                  'Turn unused space into income',
                  'Set your own availability',
                  'Approve bookings you want',
                  'No long-term commitments',
                  'Flexible and simple',
                ].map((benefit, index) => (
                  <li key={index} className="flex items-center gap-3 text-zinc-300">
                    <Check size={20} className="text-[#D4AF37] flex-shrink-0" />
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
              <Link 
                to="/host" 
                data-testid="hosts-cta-btn"
                className="bg-[#D4AF37] text-[#050505] font-medium px-8 py-4 rounded-full transition-all hover:bg-[#E5C564] hover:shadow-[0_0_20px_rgba(212,175,55,0.4)] inline-flex items-center gap-2"
              >
                Become a Host
                <ArrowRight size={18} />
              </Link>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800&q=80" 
                alt="Modern home interior" 
                className="rounded-2xl w-full object-cover aspect-[4/3]"
              />
              <div className="absolute inset-0 rounded-2xl border border-[#D4AF37]/20" />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits for Guests Section */}
      <section className="py-24 md:py-32 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="order-2 lg:order-1 relative">
              <img 
                src="https://images.unsplash.com/photo-1571902943202-507ec2618e8f?w=800&q=80" 
                alt="Person in home gym" 
                className="rounded-2xl w-full object-cover aspect-[4/3]"
              />
              <div className="absolute inset-0 rounded-2xl border border-[#D4AF37]/20" />
            </div>
            <div className="order-1 lg:order-2">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#D4AF37] mb-4">For Guests</p>
              <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-light text-white mb-6">
                Discover Private Spaces Near You
              </h2>
              <ul className="space-y-4 mb-8">
                {[
                  'Private and flexible spaces',
                  'No memberships required',
                  'Unique local experiences',
                  'Easy booking process',
                ].map((benefit, index) => (
                  <li key={index} className="flex items-center gap-3 text-zinc-300">
                    <Check size={20} className="text-[#D4AF37] flex-shrink-0" />
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
              <Link 
                to="/guest" 
                data-testid="guests-cta-btn"
                className="bg-[#D4AF37] text-[#050505] font-medium px-8 py-4 rounded-full transition-all hover:bg-[#E5C564] hover:shadow-[0_0_20px_rgba(212,175,55,0.4)] inline-flex items-center gap-2"
              >
                Find a Space
                <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits for Affiliates Section */}
      <section className="py-24 md:py-32 border-t border-[#D4AF37]/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#D4AF37] mb-4">For Affiliates</p>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-light text-white mb-6">
              Earn By Growing 100Nook
            </h2>
            <ul className="space-y-4 mb-8 text-left max-w-md mx-auto">
              {[
                'Refer hosts to the platform',
                'Earn commission per booking',
                'Recurring income potential',
                'Help grow your community',
              ].map((benefit, index) => (
                <li key={index} className="flex items-center gap-3 text-zinc-300">
                  <Check size={20} className="text-[#D4AF37] flex-shrink-0" />
                  <span>{benefit}</span>
                </li>
              ))}
            </ul>
            <Link 
              to="/affiliate" 
              data-testid="affiliates-cta-btn"
              className="bg-[#D4AF37] text-[#050505] font-medium px-8 py-4 rounded-full transition-all hover:bg-[#E5C564] hover:shadow-[0_0_20px_rgba(212,175,55,0.4)] inline-flex items-center gap-2"
            >
              Become an Affiliate
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Example Spaces Grid */}
      <section className="py-24 md:py-32 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#D4AF37] mb-4">Explore</p>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-light text-white">
              Example Spaces
            </h2>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {spaceCategories.map((category, index) => (
              <Link 
                to="/search" 
                key={index}
                className="group relative aspect-square rounded-2xl overflow-hidden"
              >
                <img 
                  src={category.image} 
                  alt={category.name}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
                <div className="absolute inset-0 border border-[#D4AF37]/0 group-hover:border-[#D4AF37]/40 rounded-2xl transition-all duration-300" />
                <div className="absolute bottom-4 left-4 right-4">
                  <div className="flex items-center gap-2">
                    <category.icon size={18} className="text-[#D4AF37]" />
                    <span className="text-white font-medium">{category.name}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-24 md:py-32 border-t border-[#D4AF37]/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-light text-white mb-4">
            Your Space. Your Community.
          </h2>
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-light text-[#D4AF37] mb-10">
            Your Opportunity.
          </h2>
          
          <div className="flex flex-wrap justify-center gap-4">
            <Link 
              to="/become-host" 
              data-testid="final-become-host-btn"
              className="bg-[#D4AF37] text-[#050505] font-medium px-8 py-4 rounded-full transition-all hover:bg-[#E5C564] hover:shadow-[0_0_20px_rgba(212,175,55,0.4)] inline-flex items-center gap-2"
            >
              Become a Host
              <ArrowRight size={18} />
            </Link>
            <Link 
              to="/search" 
              data-testid="final-find-space-btn"
              className="bg-transparent text-[#D4AF37] border border-[#D4AF37]/30 font-medium px-8 py-4 rounded-full transition-all hover:border-[#D4AF37] hover:bg-[#D4AF37]/5 inline-flex items-center gap-2"
            >
              Find a Space
            </Link>
            <Link 
              to="/affiliate" 
              data-testid="final-affiliate-btn"
              className="bg-transparent text-white border border-white/20 font-medium px-8 py-4 rounded-full transition-all hover:border-white/40 hover:bg-white/5 inline-flex items-center gap-2"
            >
              Become an Affiliate
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0A0A0A] border-t border-[#D4AF37]/10 py-12">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <p className="text-zinc-500 text-sm">© 2026 100Nook. All rights reserved.</p>
            <div className="flex flex-wrap justify-center gap-6 text-sm text-zinc-400">
              <Link to="/search" className="hover:text-white transition-colors">Explore Spaces</Link>
              <Link to="/affiliate" className="hover:text-[#D4AF37] transition-colors">Become an Affiliate</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
              <Link to="/faqs" className="hover:text-white transition-colors">FAQs</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
